import { AddBusinessOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { UserRenderInvoiceGroupsList } from './userRenderInvoiceGroupsList';

export const UserCreateInvoiceGroupSummary = () => {
  const isMounted = useIsMounted();
  const { t } = useTranslation(['user']);
  const { watch } = useFormContext();

  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[] | []>([]);
  const [showInvoiceGroups, setShowInvoiceGroups] = useState<boolean>(false);

  const myInvoiceGroup: InvoiceGroup[] | null = watch('invoiceGroups');

  const { control } = useFormContext();
  const { append: appendInvoiceGroup, remove: removeInvoiceGroup } = useFieldArray({ control, name: 'invoiceGroups' });

  const removeMyInvoice = (ownerId: number) => {
    if (myInvoiceGroup) {
      const removeIndex = myInvoiceGroup.findIndex((item) => item.owner_id === ownerId);
      if (removeIndex !== -1) {
        removeInvoiceGroup(removeIndex);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllInvoiceGroups(0, 500, 'name', 'asc', '');
        if (isMounted()) {
          setInvoiceGroups(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    fetchData();
  }, [isMounted]);

  const filteredInvoiceGroups = useMemo(() => {
    return invoiceGroups.filter((item) => !myInvoiceGroup?.some((myItem) => myItem.owner_id === item.owner_id));
  }, [invoiceGroups, myInvoiceGroup]);

  return (
    <Alert
      severity='info'
      icon={false}
      sx={{
        border: (thm) => `1px solid ${thm.palette.divider}`,
        '& .MuiAlert-message': { width: '100% !important' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 2 }}>
        <AlertTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <AddBusinessOutlined fontSize='small' sx={{ color: (thm) => thm.palette.info.main, mr: 1 }} />
          {t('user:invoiceGroup.invoiceGroups')}
        </AlertTitle>
      </Box>
      {!showInvoiceGroups && (
        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={12} mb={1}>
            <Typography gutterBottom sx={{ fontStyle: 'italic' }}>
              {t('user:invoiceGroup.noInvoiceGroups')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button variant='outlined' size='small' onClick={() => setShowInvoiceGroups(true)}>
              {t('common:button.add')}
            </Button>
          </Grid>
        </Grid>
      )}
      {showInvoiceGroups && (
        <Grid container spacing={3}>
          <Grid item sm={12} lg={6}>
            <Typography gutterBottom>
              <Box component='span' sx={{ fontWeight: 500 }}>
                {invoiceGroups?.length > 1 ? t('user:invoiceGroup.selectInvoiceGroups') : t('user:invoiceGroup.selectInvoiceGroup')}
              </Box>
              <Box component='small' sx={{ fontWeight: 400 }}>
                ({Array.isArray(filteredInvoiceGroups) ? filteredInvoiceGroups.length : 0})
              </Box>
            </Typography>
            {filteredInvoiceGroups.length > 0 && (
              <UserRenderInvoiceGroupsList invoiceGroupList={filteredInvoiceGroups} onAdd={(item) => appendInvoiceGroup(item)} />
            )}
          </Grid>

          <Grid item sm={12} lg={6}>
            <Typography gutterBottom>
              <Box component='span' sx={{ fontWeight: 500 }}>
                {t('user:invoiceGroup.added')}
              </Box>
              <Box component='small' sx={{ fontWeight: 400 }}>
                ({Array.isArray(myInvoiceGroup) ? myInvoiceGroup.length : 0})
              </Box>
            </Typography>
            {myInvoiceGroup != null && myInvoiceGroup?.length > 0 && (
              <UserRenderInvoiceGroupsList invoiceGroupList={myInvoiceGroup} onRemove={(id) => removeMyInvoice(id)} />
            )}
          </Grid>

          <Grid item xs={12} mt={2}>
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                setShowInvoiceGroups(false);
                removeInvoiceGroup();
              }}
            >
              {t('common:button.cancel')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Alert>
  );
};
